/**
 * Analytics (M5a): each connected channel's YouTube figures, as YouTube returns them. Totals for a range are YouTube's own totals;
 * the only figure ContentOps calculates is the "All channels" sum of simple counts (labelled "Sum of channels"); averages are never
 * combined across channels. Plain SVG/CSS, no chart library; every chart has a table alternative.
 */
import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { Link } from "../../app/router";
import { ApiError } from "../../api/errors";
import type { AnalyticsAccount, AnalyticsOverview, AnalyticsPlaylist, ChannelMetrics, SyncCall, SyncRun, TopVideo } from "../../api/types";
import { Icon } from "../../components/Icon";
import { LegalLinks } from "../../components/LegalLinks";
import { ResponsiveTable, type Column } from "../../components/ResponsiveTable";
import { useToast } from "../../components/toast";
import { Badge, Button, Card, EmptyState, PageHeader, QueryView, Segmented } from "../../components/ui";
import { useApi, useCache, useMutation } from "../../data/hooks";
import { useAccounts, useAnalytics, useAnalyticsRuns } from "../../data/queries";
import { dateTime, relative } from "../../lib/format";
import type { Tone } from "../../lib/status";
import { contentTypeLabel, count, DASH, day, duration, hours, paramPairs, percent, privacy, RANGE_LABEL, timeOfDay } from "./format";

type Range = "7d" | "28d" | "90d" | "lifetime";
type Channel = { id: string; name: string };
export const SUM_BADGE_HINT = "Sum of the channels' YouTube figures, calculated by ContentOps.";
export const PER_CHANNEL_ONLY = "Shown per channel only";

export function AnalyticsScreen() {
  const [range, setRange] = useState<Range>("28d");
  const [account, setAccount] = useState("all");
  const q = useAnalytics(range, account);
  const accounts = useAccounts().data;
  const channels = useMemo(() => mergeChannels(q.data?.accounts ?? [], accounts ?? []), [q.data?.accounts, accounts]);
  const refresh = useRefreshNow(account);
  const pickerId = useId();
  return (
    <div className="stack-lg">
      <PageHeader title="Analytics" subtitle="Your channels' figures from YouTube." actions={
        <div className="an-controls">
          <div className="an-picker">
            <label htmlFor={pickerId} className="sr-only">Channel</label>
            <select id={pickerId} value={account} onChange={(e) => setAccount(e.target.value)}>
              <option value="all">All channels</option>
              {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              {account !== "all" && !channels.some((c) => c.id === account) && <option value={account}>This channel</option>}
            </select>
          </div>
          <Segmented label="Range" hideLabel value={range} onChange={setRange}
            options={[{ value: "7d", label: "7 d", hint: "Last 7 days" }, { value: "28d", label: "28 d", hint: "Last 28 days" }, { value: "90d", label: "90 d", hint: "Last 90 days" }, { value: "lifetime", label: "Lifetime" }]} />
        </div>
      } />
      <QueryView q={q}>{(d) => {
        if (!channels.length && !d.accounts.length && isEmpty(d)) {
          return (
            <EmptyState icon="chart" title="No channels connected" action={<Link to="/settings/accounts" className="btn btn-secondary btn-md">Connect a channel</Link>}>
              Connect a YouTube channel in Settings to see its views, watch time, top videos and playlists here.
            </EmptyState>
          );
        }
        return (
          <>
            {isEmpty(d)
              ? <EmptyState icon="chart" title={d.freshness.last_synced_at ? "No figures for this range" : "Not refreshed yet"} action={<Button variant="secondary" icon="refresh" busy={refresh.busy} onClick={refresh.run}>Refresh now</Button>}>
                  {d.freshness.last_synced_at
                    ? "YouTube has no figures for this channel and range yet. New videos usually show up after 2–3 days."
                    : "ContentOps fetches each channel's YouTube data once a day. Refresh now to fetch it straight away."}
                </EmptyState>
              : <Body d={d} channels={channels} />}
            <Footer d={d} account={account} channels={channels} refresh={refresh} />
          </>
        );
      }}</QueryView>
    </div>
  );
}

function mergeChannels(fromOverview: AnalyticsAccount[], fromAccounts: { id: string; name: string; platform: string; auth_status: string }[]): Channel[] {
  const out = new Map<string, Channel>();
  for (const a of fromOverview) out.set(a.id, { id: a.id, name: a.name });
  for (const a of fromAccounts) {
    if (a.platform === "youtube" && (a.auth_status === "connected" || a.auth_status === "expiring") && !out.has(a.id)) out.set(a.id, { id: a.id, name: a.name });
  }
  return [...out.values()];
}

function isEmpty(d: AnalyticsOverview): boolean {
  const m = d.totals.metrics;
  return !d.daily.length && !d.top_videos.length && !d.playlists.length && !d.content_types.length && m.views == null && m.estimatedMinutesWatched == null;
}

/** "Sum of channels" marker — shown wherever the server says a figure is ContentOps' sum of the channels' YouTube figures. */
export function SumBadge() {
  return <span title={SUM_BADGE_HINT}><Badge tone="info">Sum of channels</Badge></span>;
}

function Body({ d, channels }: { d: AnalyticsOverview; channels: Channel[] }) {
  const all = d.account === "all";
  const nameOf = (id: string | null | undefined) => channels.find((c) => c.id === id)?.name ?? null;
  return (
    <>
      <Totals d={d} />
      {d.daily.length > 0 && <Daily d={d} />}
      {d.content_types.length > 0 && <ContentTypes d={d} />}
      {d.top_videos.length > 0 && <TopVideos videos={d.top_videos} channelOf={all && channels.length > 1 ? nameOf : null} />}
      {d.playlists.length > 0 && <Playlists playlists={d.playlists} channelOf={all && channels.length > 1 ? nameOf : null} />}
    </>
  );
}

// ───────── totals ─────────
type Kpi = { key: keyof ChannelMetrics & string; label: string; fmt: (n: number | null | undefined) => string; average?: boolean };
const KPIS: Kpi[] = [
  { key: "views", label: "Views", fmt: count },
  { key: "engagedViews", label: "Engaged views", fmt: count },
  { key: "estimatedMinutesWatched", label: "Watch time (hours)", fmt: hours },
  { key: "averageViewDuration", label: "Avg view duration", fmt: duration, average: true },
  { key: "averageViewPercentage", label: "Avg % viewed", fmt: percent, average: true },
  { key: "likes", label: "Likes", fmt: count },
  { key: "comments", label: "Comments", fmt: count },
  { key: "shares", label: "Shares", fmt: count },
  { key: "subscribersGained", label: "Subscribers gained", fmt: count },
  { key: "subscribersLost", label: "Subscribers lost", fmt: count },
];

function Totals({ d }: { d: AnalyticsOverview }) {
  const summed = d.totals.source === "sum_of_channels";
  const hid = useId();
  return (
    <section className="stack an-totals" aria-labelledby={hid}>
      <div className="an-section-head">
        <h2 id={hid} className="an-section-title">{RANGE_LABEL[d.range] ?? d.range}</h2>
        {d.window && <span className="muted small">{day(d.window.start)} – {day(d.window.end)}</span>}
        {summed && <SumBadge />}
      </div>
      {summed && <p className="muted small an-note">{SUM_BADGE_HINT} Averages are {PER_CHANNEL_ONLY.toLowerCase()}.</p>}
      <div className="kpis an-kpis">
        {KPIS.map((k) => {
          const v = d.totals.metrics[k.key] as number | null | undefined;
          const perChannelOnly = k.average && summed && v == null;
          return (
            <div key={k.key} className="kpi" role="group" aria-label={k.label}>
              <span className="kpi-label">{k.label}</span>
              {perChannelOnly
                ? <span className="kpi-value" title={PER_CHANNEL_ONLY}><span aria-hidden>{DASH}</span><span className="sr-only">{PER_CHANNEL_ONLY}</span></span>
                : <span className="kpi-value">{k.fmt(v)}</span>}
              {perChannelOnly && <span className="kpi-note muted" aria-hidden>{PER_CHANNEL_ONLY}</span>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ───────── views per day ─────────
function Daily({ d }: { d: AnalyticsOverview }) {
  const [view, setView] = useState<"chart" | "table">("chart");
  const summed = d.daily_source === "sum_of_channels";
  return (
    <Card title="Views per day" action={
      <div className="row">
        {summed && <SumBadge />}
        <Segmented label="Show views per day as" hideLabel value={view} onChange={setView} options={[{ value: "chart", label: "Chart" }, { value: "table", label: "Table" }]} />
      </div>
    }>
      {view === "chart"
        ? <BarChart data={d.daily.map((x) => ({ label: x.date, value: x.views ?? 0 }))} />
        : (
          <div className="table-wrap">
            <table className="data-table" aria-label="Views per day">
              <thead><tr><th scope="col">Date</th><th scope="col" className="num">Views</th><th scope="col" className="num">Engaged views</th><th scope="col" className="num">Watch time (min)</th><th scope="col" className="num">Subscribers gained</th><th scope="col" className="num">Subscribers lost</th></tr></thead>
              <tbody>{d.daily.map((x) => (
                <tr key={x.date}><th scope="row" className="nowrap">{day(x.date)}</th><td className="num">{count(x.views)}</td><td className="num">{count(x.engagedViews)}</td><td className="num">{count(x.estimatedMinutesWatched)}</td><td className="num">{count(x.subscribersGained)}</td><td className="num">{count(x.subscribersLost)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
      <p className="muted small">YouTube's figures usually arrive 2–3 days late, so the most recent days can read low or zero.</p>
    </Card>
  );
}

function BarChart({ data }: { data: { label: string; value: number }[] }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  const peak = data.reduce((a, b) => (b.value > a.value ? b : a), data[0]!);
  return (
    <figure className="bars" aria-label={`Views per day from ${day(data[0]?.label)} to ${day(data.at(-1)?.label)}, highest ${count(peak.value)} on ${day(peak.label)}. Switch to Table for every value.`}>
      <div className="bars-plot" role="presentation">
        {data.map((d) => <span key={d.label} className="bar" style={{ height: `${(d.value / max) * 100}%` }} title={`${day(d.label)}: ${count(d.value)} views`} />)}
      </div>
      <figcaption className="bars-axis muted small"><span>{day(data[0]?.label)}</span><span>highest {count(peak.value)}</span><span>{day(data.at(-1)?.label)}</span></figcaption>
    </figure>
  );
}

// ───────── Shorts vs other formats ─────────
function ContentTypes({ d }: { d: AnalyticsOverview }) {
  const summed = (d.content_types_source ?? d.totals.source) === "sum_of_channels";
  const max = Math.max(1, ...d.content_types.map((c) => c.views ?? 0));
  return (
    <Card title="Shorts and other formats" action={summed ? <SumBadge /> : undefined}>
      <div className="table-wrap">
        <table className="data-table an-formats" aria-label="Shorts and other formats">
          <thead><tr><th scope="col">Format</th><th scope="col" className="num">Views</th><th scope="col" className="num">Engaged views</th><th scope="col" className="num">Watch time (h)</th></tr></thead>
          <tbody>{d.content_types.map((c) => (
            <tr key={c.content_type}>
              <th scope="row">
                <span className="an-format-name">{contentTypeLabel(c.content_type)}</span>
                <span className="an-meter" aria-hidden><span style={{ width: `${((c.views ?? 0) / max) * 100}%` }} /></span>
              </th>
              <td className="num">{count(c.views)}</td><td className="num">{count(c.engagedViews)}</td><td className="num">{hours(c.estimatedMinutesWatched)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </Card>
  );
}

// ───────── top videos + playlists ─────────
function YtLink({ href, children }: { href: string; children: ReactNode }) {
  return <a className="an-yt-link" href={href} target="_blank" rel="noopener noreferrer">{children}<span className="sr-only"> (opens YouTube in a new tab)</span><Icon name="external" size={14} /></a>;
}

function TopVideos({ videos, channelOf }: { videos: TopVideo[]; channelOf: ((id: string | null | undefined) => string | null) | null }) {
  const columns: Column<TopVideo>[] = [
    { key: "title", label: "Video", render: (v) => (
      <span className="an-title-cell">
        <YtLink href={v.url ?? `https://www.youtube.com/watch?v=${encodeURIComponent(v.video_id)}`}>{v.title || v.video_id}</YtLink>
        {channelOf && channelOf(v.account_id) && <span className="muted small">{channelOf(v.account_id)}</span>}
      </span>
    ) },
    { key: "published", label: "Published", render: (v) => <span className="nowrap">{v.published_at ? day(v.published_at) : DASH}</span> },
    { key: "privacy", label: "Privacy", render: (v) => privacy(v.privacy_status) },
    { key: "views", label: "Views", num: true, render: (v) => count(v.metrics.views) },
    { key: "watch", label: "Watch time (h)", num: true, render: (v) => hours(v.metrics.estimatedMinutesWatched) },
    { key: "pct", label: "Avg % viewed", num: true, render: (v) => percent(v.metrics.averageViewPercentage) },
    { key: "likes", label: "Likes", num: true, render: (v) => count(v.metrics.likes) },
  ];
  return (
    <Card title="Top videos">
      <p className="muted small">By views in this range. Titles open the video on YouTube.</p>
      <ResponsiveTable label="Top videos" columns={columns} rows={videos} rowKey={(v) => `${v.account_id ?? ""}:${v.video_id}`} />
    </Card>
  );
}

const PLAYLIST_METRICS: { key: string; label: string; fmt: (n: number | null | undefined) => string }[] = [
  { key: "views", label: "Views", fmt: count },
  { key: "estimatedMinutesWatched", label: "Watch time (h)", fmt: hours },
  { key: "averageViewDuration", label: "Avg view duration", fmt: duration },
  { key: "playlistStarts", label: "Playlist starts", fmt: count },
  { key: "playlistViews", label: "Playlist views", fmt: count },
  { key: "playlistSaves", label: "Playlist saves", fmt: count },
];

function Playlists({ playlists, channelOf }: { playlists: AnalyticsPlaylist[]; channelOf: ((id: string | null | undefined) => string | null) | null }) {
  // Only the metrics YouTube returned for at least one playlist get a column (a rejected metric is simply absent).
  const present = new Set(playlists.flatMap((p) => Object.keys(p.metrics ?? {})));
  const metricCols = [...PLAYLIST_METRICS.filter((m) => present.has(m.key)), ...[...present].filter((k) => !PLAYLIST_METRICS.some((m) => m.key === k)).map((k) => ({ key: k, label: k.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()), fmt: count }))];
  const columns: Column<AnalyticsPlaylist>[] = [
    { key: "title", label: "Playlist", render: (p) => (
      <span className="an-title-cell">
        <YtLink href={p.url ?? `https://www.youtube.com/playlist?list=${encodeURIComponent(p.playlist_id)}`}>{p.title || p.playlist_id}</YtLink>
        {channelOf && channelOf(p.account_id) && <span className="muted small">{channelOf(p.account_id)}</span>}
      </span>
    ) },
    { key: "privacy", label: "Privacy", render: (p) => privacy(p.privacy_status) },
    { key: "items", label: "Videos", num: true, render: (p) => count(p.item_count) },
    ...metricCols.map((m): Column<AnalyticsPlaylist> => ({ key: m.key, label: m.label, num: true, render: (p) => (p.metrics ? m.fmt(p.metrics[m.key]) : DASH) })),
  ];
  return (
    <Card title="Playlists">
      {playlists.some((p) => !p.metrics) && <p className="muted small">“{DASH}” means YouTube has no figures for that playlist yet.</p>}
      <ResponsiveTable label="Playlists" columns={columns} rows={playlists} rowKey={(p) => `${p.account_id ?? ""}:${p.playlist_id}`} />
    </Card>
  );
}

// ───────── footer: disclosure, freshness, Refresh now, sync history ─────────
type Refresh = { run: () => void; busy: boolean };

function When({ iso }: { iso: string | null | undefined }) {
  if (!iso) return <>never</>;
  return <time dateTime={iso} title={dateTime(iso)}>{relative(iso)}</time>;
}

const RUN_TONE: Record<string, Tone> = { ok: "ok", partial: "attention", failed: "danger", running: "info" };
const RUN_LABEL: Record<string, string> = { ok: "OK", partial: "Partly done", failed: "Failed", running: "Running" };

function Footer({ d, account, channels, refresh }: { d: AnalyticsOverview; account: string; channels: Channel[]; refresh: Refresh }) {
  const shown = d.accounts.filter((a) => account === "all" || a.id === account);
  return (
    <Card className="an-footer" as="section">
      <div className="an-fresh">
        <div className="stack-sm">
          <p className="small">
            {d.freshness.data_through ? <>Data through <b>{day(d.freshness.data_through)}</b></> : "No YouTube figures yet"}
            <span aria-hidden> · </span>Last refreshed <b><When iso={d.freshness.last_synced_at} /></b>
          </p>
          {shown.length > 1 && (
            <ul className="an-channels" aria-label="Channels">
              {shown.map((a) => (
                <li key={a.id} className="small">
                  <b>{a.name}</b>: refreshed <When iso={a.last_synced_at} />{a.data_through ? <>, data through {day(a.data_through)}</> : null}
                  {a.last_run_status && a.last_run_status !== "ok" && <> <Badge tone={RUN_TONE[a.last_run_status] ?? "neutral"}>{RUN_LABEL[a.last_run_status] ?? a.last_run_status}</Badge></>}
                  {a.analytics_enabled === false && <> · <Link to="/settings/accounts" className="text-link inline">Analytics not granted — reconnect</Link></>}
                </li>
              ))}
            </ul>
          )}
          {shown.length === 1 && shown[0]!.analytics_enabled === false && (
            <p className="audit-alert small" role="status"><Icon name="lock" size={18} /> <span>Analytics not granted for this channel. <Link to="/settings/accounts" className="text-link inline">Reconnect it</Link> and allow YouTube Analytics to see views and watch time.</span></p>
          )}
        </div>
        <Button variant="secondary" icon="refresh" busy={refresh.busy} onClick={refresh.run}>Refresh now</Button>
      </div>
      {d.disclosure && <p className="muted small an-disclosure">{d.disclosure}</p>}
      <SyncHistory account={account} channels={channels} />
      <LegalLinks describe className="an-legal" />
    </Card>
  );
}

function SyncHistory({ account, channels }: { account: string; channels: Channel[] }) {
  const [open, setOpen] = useState(false);
  const rid = useId();
  const q = useAnalyticsRuns(account, open);
  return (
    <div className="an-history">
      <button type="button" className="an-history-toggle" aria-expanded={open} aria-controls={rid} onClick={() => setOpen(!open)}>
        <Icon name="chevronDown" size={18} className={open ? "is-open" : undefined} /> Sync history
      </button>
      {open && (
        <div id={rid} className="stack">
          <p className="muted small">Every YouTube API call ContentOps made for these figures: which API and endpoint, the parameters sent (never any keys), the rows returned and the HTTP status.</p>
          <QueryView q={q}>{(runs) => runs.length
            ? <ol className="an-runs">{runs.map((r, i) => <RunItem key={r.id} run={r} channel={channels.find((c) => c.id === r.account_id)?.name ?? null} initiallyOpen={i === 0} />)}</ol>
            : <p className="muted small">No syncs yet.</p>}</QueryView>
        </div>
      )}
    </div>
  );
}

function RunItem({ run, channel, initiallyOpen }: { run: SyncRun; channel: string | null; initiallyOpen: boolean }) {
  const [open, setOpen] = useState(initiallyOpen);
  const cid = useId();
  const secs = run.started_at && run.finished_at ? Math.max(0, Math.round((Date.parse(run.finished_at) - Date.parse(run.started_at)) / 1000)) : null;
  const columns: Column<SyncCall>[] = [
    { key: "endpoint", label: "Call", render: (c) => <span className="an-call"><span className="an-api">{c.api === "analytics" ? "Analytics API" : c.api === "data" ? "Data API" : c.api}</span> <code>{c.endpoint}</code></span> },
    { key: "params", label: "Params", render: (c) => {
      const dropped = c.dropped_metrics ?? (Array.isArray(c.params?.dropped_metrics) ? (c.params!.dropped_metrics as unknown[]).map(String) : []);
      return (
        <span className="an-params">
          {paramPairs(c.params).map((p) => <code key={p.full} title={p.full}>{p.text}</code>)}
          {dropped.length > 0 && <span className="an-dropped">Dropped: {dropped.join(", ")}</span>}
        </span>
      );
    } },
    { key: "rows", label: "Rows", num: true, render: (c) => count(c.rows) },
    { key: "status", label: "HTTP", num: true, render: (c) => <span className={c.status != null && c.status >= 400 ? "an-http-bad" : undefined}>{c.status ?? DASH}</span> },
  ];
  return (
    <li className="an-run">
      <button type="button" className="an-run-head" aria-expanded={open} aria-controls={cid} onClick={() => setOpen(!open)}>
        <span className="list-main">
          <span className="list-title">{run.started_at ? dateTime(run.started_at) : "Not started"}{channel ? ` · ${channel}` : ""}</span>
          <span className="list-sub">{run.calls.length} call{run.calls.length === 1 ? "" : "s"}{secs != null ? ` · ${secs} s` : ""}</span>
        </span>
        <Badge tone={RUN_TONE[run.status] ?? "neutral"}>{RUN_LABEL[run.status] ?? run.status}</Badge>
      </button>
      {run.error && <p className="pub-error"><Icon name="alert" size={18} /> {run.error}</p>}
      {open && (
        <div id={cid}>
          {run.calls.length
            ? <ResponsiveTable className="an-calls" label={`API calls, ${run.started_at ? dateTime(run.started_at) : run.id}`} columns={columns} rows={run.calls} rowKey={(c) => `${run.calls.indexOf(c)}`} />
            : <p className="muted small">No calls recorded.</p>}
        </div>
      )}
    </li>
  );
}

// ───────── Refresh now: POST sync → toast → poll the runs → refetch ─────────
function retryAtOf(e: ApiError): string | null {
  const d = e.details as { retry_at?: unknown } | undefined;
  return typeof d?.retry_at === "string" ? d.retry_at : null;
}

export function useRefreshNow(account: string): Refresh {
  const api = useApi();
  const cache = useCache();
  const toast = useToast();
  const [pending, setPending] = useState<{ ids: string[]; account: string } | null>(null);
  const start = useMutation(() => api.syncAnalytics(account === "all" ? undefined : account), {
    onSuccess: (r) => {
      if (!r.runs.length) { toast.show({ tone: "info", text: "Nothing to refresh: no channel is connected." }); return; }
      toast.show({ tone: "info", text: r.runs.length === 1 ? "Refresh started. It usually takes a minute." : `Refresh started for ${r.runs.length} channels. It usually takes a minute.` });
      setPending({ ids: r.runs.map((x) => x.run_id), account });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 429) {
        const at = retryAtOf(e);
        toast.show({ tone: "info", text: at ? `Refreshed a moment ago. Try again at ${timeOfDay(at)}.` : "Refreshed a moment ago. Try again in a few minutes." });
      } else toast.error(e, "Couldn't refresh");
    },
  });
  useEffect(() => {
    if (!pending) return;
    let stopped = false;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = async () => {
      tries++;
      try {
        const runs = await api.analyticsRuns({ account: pending.account, limit: 20 });
        if (stopped) return;
        const mine = runs.filter((r) => pending.ids.includes(r.id));
        if (mine.length === pending.ids.length && mine.every((r) => r.finished_at || !["running", "queued", "pending"].includes(r.status))) {
          cache.invalidate("analytics");
          cache.invalidate("accounts");
          if (mine.some((r) => r.status === "failed")) toast.show({ tone: "danger", text: "The refresh failed. Sync history has the details." });
          else if (mine.some((r) => r.status === "partial")) toast.show({ tone: "info", text: "Refreshed, but YouTube left some figures out. Sync history has the details." });
          else toast.ok("YouTube data refreshed.");
          setPending(null);
          return;
        }
      } catch { /* keep polling; a blip shouldn't end the wait */ }
      if (stopped) return;
      if (tries >= 40) {
        cache.invalidate("analytics");
        toast.show({ tone: "info", text: "Still refreshing. The figures update here when it finishes." });
        setPending(null);
        return;
      }
      timer = setTimeout(() => void check(), 3000);
    };
    void check();
    return () => { stopped = true; if (timer) clearTimeout(timer); };
  }, [pending, api, cache, toast]);
  return { run: () => void start.mutate(), busy: start.pending || pending !== null };
}
